import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useSite } from '../../lib/site';
import { Alert, Card, SelectField } from '../../components/ui';
import { ErrorLine } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { useMemberPoints, usePoints } from './api';

function SiteRow({ memberId, siteId, siteName, value, disabled, onSaved }: { memberId: string; siteId: string; siteName: string; value: string; disabled: boolean; onSaved: (siteId: string, pointId: string | null) => void }) {
  const points = usePoints(siteId);
  const toast = useToast();
  const save = useMutation({
    mutationFn: (pointId: string | null) => api.put(`/evacuation/members/${memberId}/point`, { body: { siteId, pointId } }),
    onSuccess: (_r, pointId) => {
      onSaved(siteId, pointId);
      toast(t('evac.member.saved'));
    },
  });
  const active = (points.data ?? []).filter((p) => p.active);
  return (
    <div>
      <ErrorLine error={save.error} />
      <SelectField
        label={t('evac.member.forSite', { site: siteName })}
        disabled={disabled || save.isPending}
        value={value}
        onChange={(e) => save.mutate(e.target.value || null)}
        {...(points.data && active.length === 0 ? { hint: t('evac.member.noPoints') } : {})}
      >
        <option value="">{t('evac.member.useDefault')}</option>
        {active.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </SelectField>
    </div>
  );
}

export interface MemberMusterPointProps {
  memberId: string;
  /** Needs the evacuation.manage permission; the server checks it too. */
  canEdit: boolean;
}

/**
 * Member drawer tab: the person's own muster point at each site (or "use the default"). Exported for the member drawer (W23).
 * The current choices load with GET /evacuation/members/:memberId/point. Each choice is saved at once with PUT /evacuation/members/:memberId/point.
 */
export function MemberMusterPoint({ memberId, canEdit }: MemberMusterPointProps) {
  const current = useMemberPoints(memberId);
  const overrides = current.data ?? {};
  const { sites } = useSite();
  const [saved, setSaved] = useState<Record<string, string | null>>({});
  const usable = sites.filter((s) => !s.isRemote);
  return (
    <Card title={t('evac.member.title')} description={t('evac.member.description')}>
      <div className="space-y-4">
        {current.isError ? <ErrorLine error={current.error} /> : null}
        {usable.length === 0 ? <Alert kind="info">{t('evac.member.noSites')}</Alert> : null}
        {usable.map((s) => (
          <SiteRow
            key={s.id}
            memberId={memberId}
            siteId={s.id}
            siteName={s.name}
            value={(s.id in saved ? saved[s.id] : overrides[s.id]) ?? ''}
            disabled={!canEdit || !current.data}
            onSaved={(siteId, pointId) => setSaved((cur) => ({ ...cur, [siteId]: pointId }))}
          />
        ))}
      </div>
    </Card>
  );
}
